/**
 * 화면에 보이는 시각은 전부 **KST** 다.
 *
 * D1 은 감사 시각·세션 시각·하네스 판정 시각을 UTC ISO(`2026-09-09T06:57:13.086Z`)로
 * 적는다. 화면은 그 문자열에서 `T` 를 공백으로 바꿔 그대로 찍고 있었다 —
 * 오후 3시 57분에 한 로그인이 **오전 6시 57분**으로 보였다. 감사 로그에서 이건
 * 단순한 보기 흉함이 아니라 **없는 사건을 만들어 낸다**: "새벽 여섯 시에 누가
 * 들어왔다" 로 읽힌다.
 *
 * 반대로 업무 날짜(`received_at`·`shipped_at`)는 **이미 KST 날짜**다(엑셀·사람 입력
 * 원문). 그것까지 9시간 밀면 없던 하루가 생긴다. 그래서 규칙은 하나다 —
 *
 * | 값의 모양 | 처리 |
 * |-----------|------|
 * | `YYYY-MM-DD` (업무 날짜) | **그대로 둔다.** 이미 KST 이고 원문이다 |
 * | 시각이 붙은 ISO | KST 로 옮겨 보여 준다 |
 * | 해석되지 않는 문자열 | **원문 그대로 돌려준다.** 추정해서 고쳐 쓰지 않는다(SOUL S2·S3) |
 *
 * 한국 표준시는 서머타임이 없으므로 고정 오프셋으로 정확하다.
 * 서버측 정렬 키(`apps/api/src/db/devices.ts`)도 같은 상수를 쓴다 — KST 의 원천은 여기다.
 */

/** UTC → KST 고정 오프셋. 대한민국은 1988년 이후 서머타임이 없다 */
export const KST_OFFSET_MS = 9 * 60 * 60 * 1000;

/** 업무 날짜(엑셀·사람 입력)의 모양. 이 모양이면 이미 KST 이므로 손대지 않는다 */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function shifted(value: string): Date | null {
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return null;
  return new Date(ms + KST_OFFSET_MS);
}

/**
 * 시각을 KST 로 보여 준다 — `2026-09-09 15:57`.
 *
 * @param withSeconds 초까지 보여 줄 것인가(하네스 판정·감사 로그처럼 순서가 중요한 표)
 */
export function formatKstStamp(
  value: string | null | undefined,
  withSeconds = false,
): string {
  if (value === null || value === undefined || value === "") return "";
  if (DATE_ONLY.test(value)) return value; // 업무 날짜 — 원문 보존
  const d = shifted(value);
  if (d === null) return value; // 해석 불가 — 원문 보존
  const iso = d.toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, withSeconds ? 19 : 16)}`;
}

/**
 * 날짜만 KST 로 보여 준다 — `2026-09-09`.
 *
 * UTC 로 자정 전후(한국 시간 0~9시)에 찍힌 시각을 `slice(0, 10)` 으로 자르면
 * **하루가 앞으로 밀린다.** 승인 시각·발급 시각처럼 "며칠에 있었나" 만 보여 주는
 * 자리에서는 이 함수를 쓴다.
 */
export function formatKstDay(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "";
  if (DATE_ONLY.test(value)) return value;
  const d = shifted(value);
  if (d === null) return value;
  return d.toISOString().slice(0, 10);
}

/**
 * 오늘(KST). 입고일·출고일 입력칸의 기본값이다.
 *
 * `new Date().toISOString().slice(0, 10)` 은 한국 시간 아침 9시 이전에 **어제**를
 * 내놓는다. 아침에 입고 등록을 하면 기본값이 전날이 되고, 사람은 기본값을 잘
 * 고치지 않는다 — 날짜가 하루 밀린 채로 저장된다.
 */
export function todayKst(now: Date = new Date()): string {
  return new Date(now.getTime() + KST_OFFSET_MS).toISOString().slice(0, 10);
}

/** 파일 이름에 쓰는 시각 도장 — `20260909-1557` (KST) */
export function kstFileStamp(now: Date = new Date()): string {
  const iso = new Date(now.getTime() + KST_OFFSET_MS).toISOString();
  return `${iso.slice(0, 10).replace(/-/g, "")}-${iso.slice(11, 16).replace(":", "")}`;
}
